import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Timeline } from "./display";
import { formatDateTime } from "../lib/dates";

/** Linha de core.audit_log (auditoria imutável). */
export interface AuditEntry {
  id: number;
  org_id: string | null;
  actor: string | null;
  action: string;
  entity: string;
  entity_id: string | null;
  data: Record<string, unknown>;
  at: string;
  actor_email?: string | null;
}

export interface AuditQuery {
  orgId: string;
  entity?: string;
  entityId?: string;
  limit?: number;
}

/** O app injeta como ler a auditoria (via @vixio/data); a UI não conhece o Supabase. */
export type AuditLoader = (q: AuditQuery) => Promise<AuditEntry[]>;

const Ctx = createContext<AuditLoader | null>(null);

export function AuditProvider({ load, children }: { load: AuditLoader; children: ReactNode }) {
  return <Ctx.Provider value={load}>{children}</Ctx.Provider>;
}

const ACTION_LABELS: Record<string, string> = {
  "organization.created": "Organização criada",
  "organization.updated": "Organização atualizada",
  "member.set": "Papel de membro definido",
  "member.removed": "Membro removido",
  "person.upserted": "Cadastro salvo",
  "person.exported": "Dados exportados (LGPD)",
  "person.anonymized": "Pessoa anonimizada (LGPD)",
  "consent.recorded": "Consentimento registrado",
  "issuer.upserted": "Emissor salvo",
  "setting.set": "Configuração alterada",
  "feature_flag.set": "Módulo ligado ou desligado",
  "invitation.created": "Convite enviado",
  "invitation.revoked": "Convite revogado",
  "invitation.accepted": "Convite aceito",
  "gate.access_granted": "Entrada nos painéis de infraestrutura",
  "gate.access_denied": "Entrada nos painéis de infraestrutura negada",
  "gate.logout": "Saída dos painéis de infraestrutura",
  "finance.receivable_created": "Título a receber criado",
  "finance.payment_received": "Recebimento registrado",
  "finance.payment_reversed": "Recebimento estornado",
  "finance.receivable_canceled": "Título cancelado",
  "finance.payable_created": "Conta a pagar criada",
  "finance.payable_paid": "Pagamento registrado",
  "finance.statement_imported": "Extrato importado",
  "finance.account_upserted": "Conta bancária salva",
  "sales.opportunity_created": "Oportunidade criada",
  "sales.opportunity_updated": "Oportunidade atualizada",
  "sales.stage_changed": "Etapa alterada",
  "sales.proposal_created": "Proposta criada",
  "sales.proposal_sent": "Proposta enviada",
  "sales.proposal_accepted": "Proposta aceita",
  "sales.proposal_public_viewed": "Proposta vista pelo link",
  "sales.price_floor_set": "Piso de preço calculado",
  "sales.proposal_options_set": "Opções da proposta salvas",
  "billing.subscription_created": "Assinatura criada",
  "fiscal.invoice_created": "Nota fiscal criada",
  "fiscal.invoice_manual_issue": "Emissão manual registrada",
  "fiscal.issuer_config_set": "Configuração fiscal alterada",
  "offers.role_cost_set": "Custo por papel alterado",
};

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const KEY_LABELS: Record<string, string> = {
  total_cents: "total", amount_cents: "valor", installments: "parcelas", renegotiation: "renegociação", reason: "motivo",
  role: "papel", email: "e-mail", module: "módulo", enabled: "ligado", key: "chave", slug: "identificador", from: "de", to: "para",
};

/**
 * Cada pacote de UI de módulo registra os rótulos pt-BR das próprias ações de auditoria
 * (ex.: `registerAuditLabels({ "img.feedback_template_set": "Modelo da devolutiva alterado" })`).
 * Rótulos já existentes não são sobrescritos.
 */
export function registerAuditLabels(labels: Record<string, string>): void {
  for (const [k, v] of Object.entries(labels)) if (!(k in ACTION_LABELS)) ACTION_LABELS[k] = v;
}

export function auditLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function summarize(data: Record<string, unknown>): string | null {
  const entries = Object.entries(data ?? {}).filter(
    ([k, v]) => v !== null && v !== undefined && typeof v !== "object" && !/(^|_)id$/.test(k) && v !== false,
  );
  if (entries.length === 0) return null;
  return entries
    .map(([k, v]) => {
      const label = KEY_LABELS[k] ?? k.replace(/_cents$/, "").replace(/_/g, " ");
      const value = k.endsWith("_cents") && typeof v === "number" ? brl.format(v / 100) : v === true ? "sim" : String(v);
      return `${label}: ${value}`;
    })
    .join(" · ");
}

export function AuditTimeline({ orgId, entity, entityId, limit = 50, empty }: AuditQuery & { empty?: ReactNode }) {
  const load = useContext(Ctx);
  const q = useQuery({
    queryKey: ["core", "audit_log", orgId, entity ?? null, entityId ?? null, limit],
    queryFn: () => (load ? load({ orgId, entity, entityId, limit }) : Promise.resolve([])),
    enabled: Boolean(load),
  });
  if (!load) return <p className="muted">Auditoria indisponível.</p>;
  if (q.isLoading) return <p className="muted">Carregando auditoria</p>;
  if (q.error) return <p className="text-crit">A auditoria só é visível para administradores.</p>;
  return (
    <Timeline
      empty={empty ?? "Nenhum registro de auditoria."}
      items={(q.data ?? []).map((e) => ({
        id: e.id,
        title: auditLabel(e.action),
        meta: `${formatDateTime(e.at)}${e.actor_email ? ` · ${e.actor_email}` : e.actor ? "" : " · sistema"}`,
        body: summarize(e.data),
        tone: e.action.includes("anonymized") || e.action.includes("removed") || e.action.includes("revoked") ? "crit" : "neutral",
      }))}
    />
  );
}
